
import { Draft, produce } from "immer";
import { useDebugValue, useSyncExternalStore } from "react";

export type StateCreator<T> = (
  setState: (updater: ProduceParam<T>) => void,
  getState: () => T
) => T;

type ExtractState<S> = S extends { getState: () => infer T } ? T : never;

export type UseBoundStore<S> = {
  (): ExtractState<S>;
  <U>(selector: (state: ExtractState<S>) => U): U;
} & S;

export const create = <T>(createState: StateCreator<T>) => {
  const api = createStore(createState);

  const useBoundStore = (selector: any) => {
    const slice = useSyncExternalStore(
      api.subscribe,
      () => selector(api.getState()),
      () => selector(api.getState())
    );
    useDebugValue(slice);
    return slice;
  };

  Object.assign(useBoundStore, api);

  return useBoundStore as UseBoundStore<typeof api>;
};

export interface StoreApi<T> {
  setState: (updater: ProduceParam<T>) => void;
  getState: () => T;
  subscribe: (listener: (state: T, prevState: T) => void) => () => void;
}

type ProduceParam<T> = ((draft: Draft<T>) => void);

export const createStore = <T>(createState: StateCreator<T>): StoreApi<T> => {
  type TState = ReturnType<typeof createState>;
  type Listener = (state: TState, prevState: TState) => void;
  let state: TState;
  const listeners: Set<Listener> = new Set();

  const setState: StoreApi<TState>["setState"] = (updater: ProduceParam<TState>) => {
    const nextState = produce(state, updater);
    const previousState = state;
    state = nextState;

    listeners.forEach((listener) => listener(state, previousState));
  };

  const getState: StoreApi<TState>["getState"] = () => state;

  const subscribe: StoreApi<TState>["subscribe"] = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const api = { setState, getState, subscribe };
  state = createState(setState, getState);

  return api;
};
